import { trashApi } from 'entities/trash/api/trashApi';
import { LoadableInterface } from 'shared/types/loadableInterface';
import { filter, map } from 'lodash-es';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { FileModel, RestoreFileParams } from 'entities/file/model/fileModel';
import {
  FolderModel,
  RestoreFolderParams,
} from 'entities/folder/model/folderModel';
import { SectionModel } from 'entities/section/model/sectionModel';
import {
  RestoreTableParams,
  TableModel,
} from 'entities/table/model/tableModel';
import { RootStore } from 'app/store/rootStore';
import {
  ContentItem,
  ExplorerEntityTypes,
  SortContentKey,
  SortContentOrder,
} from 'entities/types';

export class TrashModel implements LoadableInterface {
  public isPending = false;
  public isError = false;

  private _rootStore: RootStore;

  private _sections: SectionModel[] | null = null;
  private _folders: FolderModel[] | null = null;
  private _files: FileModel[] | null = null;
  private _tables: TableModel[] | null = null;
  private _content: ContentItem[] | null = null;
  private _sortKey: SortContentKey = SortContentKey.EXTENSION;
  private _sortOrder: SortContentOrder = SortContentOrder.DESC;

  constructor({ rootStore }: { rootStore: RootStore }) {
    this._rootStore = rootStore;
    makeAutoObservable(this);

    reaction(
      () => this.isPending,
      (isPending) => {
        this._rootStore.stateStore.isTrashExplorerLoading = isPending;
      }
    );
  }

  public async fetchTrash() {
    try {
      this.isPending = true;
      const { sections, folders, files, tables } = await trashApi.fetchTrash();

      this._sections = sections
        ? map(sections, (d) => new SectionModel(d))
        : [];
      this._folders = folders ? map(folders, (d) => new FolderModel(d)) : [];
      this._files = files ? map(files, (d) => new FileModel(d)) : [];
      this._tables = tables ? map(tables, (d) => new TableModel(d)) : [];
    } catch (e) {
      console.error(e);
    }
    this.isPending = false;
  }

  public async fetchContent() {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const res = await trashApi.fetchContent({
        sort_by: this._sortKey,
        sort_direction: this._sortOrder,
      });
      this._content = res;

      runInAction(() => {
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public get sections() {
    return this._sections;
  }
  public get folders() {
    return this._folders;
  }
  public get files() {
    return this._files;
  }
  public get tables() {
    return this._tables;
  }
  public get content() {
    return this._content;
  }

  public get isAllEmpty() {
    return (
      this?.sections?.length === 0 &&
      this?.folders?.length === 0 &&
      this?.files?.length === 0 &&
      this?.tables?.length === 0
    );
  }

  public async deleteAll() {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      await trashApi.deleteAll();

      runInAction(() => {
        this._sections = [];
        this._folders = [];
        this._files = [];
        this._tables = [];
        this._content = [];
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }

  private _localDeleteContent(ids: string[], type: ExplorerEntityTypes) {
    if (this.content) {
      this._content = filter(
        this.content,
        (item) => !(item.type === type && ids.includes(item.id))
      );
    }
  }

  public localDeleteSections(ids: string[]) {
    if (this._sections) {
      this._sections = filter(this._sections, (item) => !ids.includes(item.id));
    }
    this._localDeleteContent(ids, ExplorerEntityTypes.SECTION);
  }
  public localDeleteFolders(ids: string[]) {
    if (this._folders) {
      this._folders = filter(this._folders, (item) => !ids.includes(item.id));
    }
    this._localDeleteContent(ids, ExplorerEntityTypes.FOLDER);
  }
  public localDeleteFiles(ids: string[]) {
    if (this._files) {
      this._files = filter(this._files, (item) => !ids.includes(item.id));
    }
    this._localDeleteContent(ids, ExplorerEntityTypes.FILE);
  }
  public localDeleteTables(ids: string[]) {
    if (this._tables) {
      this._tables = filter(this._tables, (item) => !ids.includes(item.id));
    }
    this._localDeleteContent(ids, ExplorerEntityTypes.TABLE);
  }
}
